import { useCallback, useEffect, useMemo, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { toast } from "react-toastify";
import LayoutSubmaster from "../../components/layoutSubmasterConfig/layoutSubmaster";
import LocalStorage from "../../helpers/localstorage";
import { apiClientSubmaster } from "../../services/setupClientSubmaster/apiClientSubmaster";

type CompanyRow = {
  companyId: number;
  nameCompany: string;
  cnpj: string;
  logoUrl: string;
  rawLogo: string;
};

type FormMode = "create" | "update";

type CompanyForm = {
  companyId: number;
  nameCompany: string;
  cnpj: string;
  submaster_logo_url: File | null;
  logoPreview: string;
  currentLogoUrl: string;
};

const emptyForm: CompanyForm = {
  companyId: 0,
  nameCompany: "",
  cnpj: "",
  submaster_logo_url: null,
  logoPreview: "",
  currentLogoUrl: "",
};

const customStyles = {
  header: {
    style: {
      fontSize: "18px",
      fontWeight: "bold",
      color: "#0d6efd",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#f8f9fa",
      borderBottom: "1px solid #dee2e6",
    },
  },
  headCells: {
    style: {
      fontSize: "14px",
      fontWeight: "600",
      color: "#495057",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      minHeight: "58px",
      fontSize: "14px",
      "&:hover": {
        backgroundColor: "#e9f5ff",
      },
    },
  },
  cells: {
    style: {
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  pagination: {
    style: {
      borderTop: "1px solid #dee2e6",
      paddingTop: "8px",
      fontSize: "14px",
    },
  },
};

function formatCnpj(value: string) {
  const digits = String(value ?? "").replace(/\D/g, "");

  if (digits.length !== 14) {
    return digits; // Return unformatted if not 14 digits
  }

  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
}

function onlyNumbers(value: string) {
  return String(value ?? "").replace(/\D/g, "");
}

function resolveLogoUrl(value: string) {
  if (!value) {
    return "";
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `https://alexios.com.br:9597/uploads/logo/${value}`;
}

function isSuccessfulResponse(response: any) {
  return (
    response &&
    typeof response === "object" &&
    response.status >= 200 &&
    response.status < 300
  );
}

function revokeLogoPreview(value: string) {
  if (value.startsWith("blob:")) {
    URL.revokeObjectURL(value);
  }
}

function normalizeCompanies(payload: any): CompanyRow[] {
  const items = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : Array.isArray(payload?.companies)
        ? payload.companies
        : payload?.data
          ? [payload.data]
          : [];

  return items.map((item: any) => {
    const rawLogo = String(
      item?.submaster_logo_url ??
        item?.image ??
        item?.logo_url ??
        item?.logo ??
        item?.company_logo_url ??
        ""
    );

    return {
      companyId: Number(item?.companyId ?? item?.id ?? item?.company_id ?? 0),
      nameCompany: String(
        item?.nameCompany ?? item?.name_company ?? item?.companyName ?? item?.name ?? ""
      ),
      cnpj: formatCnpj(String(item?.cnpj ?? item?.CNPJ ?? "")),
      logoUrl: resolveLogoUrl(rawLogo),
      rawLogo,
    };
  });
}

function ConfigSubmasterCompanyPage() {
  const storage = useMemo(() => new LocalStorage(), []);
  const token = String(storage.getAuthToken() ?? "");
  const submasterId = Number(storage.getId() ?? 0);
  const apiClient = useMemo(() => apiClientSubmaster(token), [token]);
  const canCompanyCreate = storage.hasWebPermission("canCompanyCreate");
  const canCompanyEdit = storage.hasWebPermission("canCompanyEdit");
  const canCompanyDelete = storage.hasWebPermission("canCompanyDelete");

  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [filterText, setFilterText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>("create");
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<CompanyForm>(emptyForm);
  const [companyDeletePermissionEnabled, setCompanyDeletePermissionEnabled] = useState(false);
  const [deleteCompanyLoadingId, setDeleteCompanyLoadingId] = useState<number | null>(null);

  const filteredCompanies = companies.filter((company) => {
    const term = filterText.toLowerCase();
    return (
      company.nameCompany.toLowerCase().includes(term) ||
      company.cnpj.toLowerCase().includes(term) ||
      String(company.companyId).includes(term)
    );
  });

  const companiesWithLogo = companies.filter((company) => company.logoUrl).length;
  const isUpdateMode = formMode === "update";

  const columns: TableColumn<CompanyRow>[] = [
    {
      name: (
        <div className="d-flex align-items-center">
          <i className="bi bi-building-fill text-primary me-2"></i> Empresa
        </div>
      ),
      selector: (row) => row.nameCompany,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div className="report-person">
          {row.logoUrl ? (
            <img className="company-table-logo" src={row.logoUrl} alt={row.nameCompany} />
          ) : (
            <span className="report-person__avatar">
              {row.nameCompany.charAt(0).toUpperCase() || "E"}
            </span>
          )}
          <div className="report-person__meta">
            <span className="report-person__name">{row.nameCompany || "Sem nome"}</span>
            <small>ID {row.companyId}</small>
          </div>
        </div>
      ),
    },
    {
      name: (
        <div className="d-flex align-items-center">
          <i className="bi bi-credit-card-2-front-fill text-secondary me-2"></i> CNPJ
        </div>
      ),
      selector: (row) => row.cnpj,
      sortable: true,
      cell: (row) => row.cnpj || <span className="text-muted">Não informado</span>,
    },
    {
      name: (
        <div className="d-flex align-items-center">
          <i className="bi bi-image-fill text-info me-2"></i> Logo
        </div>
      ),
      selector: (row) => row.rawLogo,
      sortable: true,
      cell: (row) =>
        row.logoUrl ? (
          <span className="badge bg-success">Imagem ativa</span>
        ) : (
          <span className="badge bg-secondary">Sem imagem</span>
        ),
    },
    {
      name: (
        <span>
          <i className="bi bi-pencil-square me-1 text-primary"></i>
          <span className="text-primary fw-bold">Ações</span>
        </span>
      ),
      cell: (row) => (
        <div className="report-actions">
          {canCompanyEdit && <button
            type="button"
            className="btn btn-outline-primary btn-sm report-action-btn"
            onClick={() => handleOpenUpdate(row)}
            title="Editar empresa"
          >
            <i className="bi bi-pencil-fill"></i>
            <span>Editar</span>
          </button>}
          {canCompanyEdit && <button
            type="button"
            className="btn btn-outline-danger btn-sm report-action-btn"
            onClick={() => handleDeleteImage(row.companyId)}
            disabled={!row.logoUrl}
            title="Remover imagem"
          >
            <i className="bi bi-image-alt"></i>
            <span>Imagem</span>
          </button>}
          {companyDeletePermissionEnabled && canCompanyDelete && (
            <button
              type="button"
              className="btn btn-danger btn-sm report-action-btn"
              onClick={() => handleDeleteCompany(row)}
              disabled={deleteCompanyLoadingId === row.companyId}
              title="Excluir empresa permanentemente"
            >
              <i className="bi bi-trash-fill"></i>
              <span>{deleteCompanyLoadingId === row.companyId ? "Excluindo..." : "Excluir"}</span>
            </button>
          )}
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      grow: 2,
    },
  ];

  const handleListCompanies = useCallback(async () => {
    setIsLoading(true);

    try {
      const data = new FormData();
      data.append("submaster_id", String(submasterId));
      data.append("action", "list");

      const response: any = await apiClient.post(
        `/crud/companyForSubmaster/submaster_company/${submasterId}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!isSuccessfulResponse(response)) {
        throw new Error("Erro inesperado");
      }

      setCompanies(normalizeCompanies(response.data));
    } catch (error) {
      toast.error("Erro ao buscar empresas.");
    } finally {
      setIsLoading(false);
    }
  }, [apiClient, submasterId]);

  const handleLoadCompanyDeletePermission = useCallback(async () => {
    try {
      const response: any = await apiClient.post(`/companyDeletePermission`, {});
      setCompanyDeletePermissionEnabled(
        response?.data?.res === true &&
          response?.data?.data?.company_delete_permission_enabled === true
      );
    } catch (error) {
      console.error("Erro ao carregar permissão de exclusão de empresa:", error);
      setCompanyDeletePermissionEnabled(false);
    }
  }, [apiClient]);

  const handleOpenCreate = () => {
    setFormMode("create");
    setFormData((prev) => {
      revokeLogoPreview(prev.logoPreview);
      return emptyForm;
    });
    setShowForm(true);
  };

  const handleOpenUpdate = (company: CompanyRow) => {
    setFormMode("update");
    setFormData({
      companyId: company.companyId,
      nameCompany: company.nameCompany,
      cnpj: company.cnpj,
      submaster_logo_url: null,
      logoPreview: "",
      currentLogoUrl: company.logoUrl,
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setFormData((prev) => {
      revokeLogoPreview(prev.logoPreview);
      return emptyForm;
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: name === "cnpj" ? formatCnpj(value) : value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;

    setFormData((prev) => ({
      ...prev,
      submaster_logo_url: file,
      logoPreview: (() => {
        revokeLogoPreview(prev.logoPreview);
        return file ? URL.createObjectURL(file) : "";
      })(),
    }));
  };

  const validateForm = () => {
    if (!formData.nameCompany.trim()) {
      toast.warning("O nome da empresa não pode estar vazio.");
      return false;
    }

    const cnpjDigits = onlyNumbers(formData.cnpj);
    if (cnpjDigits.length !== 14) {
      toast.warning("O CNPJ deve conter 14 dígitos.");
      return false;
    }

    if (isUpdateMode && !formData.companyId) {
      toast.warning("Empresa inválida para atualização.");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    const toastId = toast.info("Processando... aguarde", {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: false,
    });

    try {
      const data = new FormData();
      data.append("submaster_id", String(submasterId));
      data.append("nameCompany", formData.nameCompany.trim());
      data.append("action", formMode);

      if (formData.submaster_logo_url) {
        data.append("submaster_logo_url", formData.submaster_logo_url);
      }

      if (isUpdateMode) {
        data.append("companyId", String(formData.companyId));
        data.append("cnpj", onlyNumbers(formData.cnpj));
      } else {
        data.append("cnpj", onlyNumbers(formData.cnpj));
      }

      const response: any = await apiClient.post(
        `/crud/companyForSubmaster/submaster_company/${submasterId}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (isSuccessfulResponse(response)) {
        toast.dismiss(toastId);
        toast.success(isUpdateMode ? "Empresa atualizada com sucesso!" : "Empresa cadastrada com sucesso!");
        handleCloseForm();
        await handleListCompanies();
      } else {
        throw new Error("Erro inesperado");
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error(isUpdateMode ? "Erro ao atualizar empresa." : "Erro ao cadastrar empresa.");
    }
  };

  const handleDeleteImage = async (companyId: number) => {
    if (!companyId) {
      toast.warning("Empresa inválida para remover imagem.");
      return;
    }

    const canDelete = window.confirm("Deseja remover a imagem desta empresa?");

    if (!canDelete) {
      return;
    }

    const toastId = toast.info("Removendo imagem... aguarde", {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: false,
    });

    try {
      const data = new FormData();
      data.append("action", "deleteImage");
      data.append("companyId", String(companyId));

      const response: any = await apiClient.post(
        `/crud/deleteCompanyImage/submaster_company/${submasterId}/${companyId}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (isSuccessfulResponse(response)) {
        toast.dismiss(toastId);
        toast.success("Imagem removida com sucesso!");
        setFormData((prev) =>
          prev.companyId === companyId
            ? {
                ...prev,
                currentLogoUrl: "",
              }
            : prev
        );
        await handleListCompanies();
      } else {
        throw new Error("Erro inesperado");
      }
    } catch (error) {
      toast.dismiss(toastId);
      toast.error("Erro ao remover imagem.");
    }
  };

  const handleDeleteCompany = async (company: CompanyRow) => {
    if (!companyDeletePermissionEnabled || !company.companyId) {
      toast.warning("A permissão para excluir empresas não está ativa.");
      return;
    }

    const confirmed = window.confirm(
      `Deseja excluir permanentemente a empresa ${company.nameCompany}?\n\n` +
        `CNPJ: ${company.cnpj || "Não informado"}\n\n` +
        "O cadastro e a imagem da empresa serão removidos. Os registros históricos serão preservados."
    );

    if (!confirmed) {
      return;
    }

    const toastId = toast.info("Excluindo empresa... aguarde", {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      closeButton: false,
    });

    try {
      setDeleteCompanyLoadingId(company.companyId);
      const data = new FormData();
      data.append("action", "delete");
      data.append("companyId", String(company.companyId));

      const response: any = await apiClient.post(
        `/crud/companyForSubmaster/submaster_company/${submasterId}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (!isSuccessfulResponse(response) || response?.data?.res !== true) {
        throw new Error(response?.data?.message || "Não foi possível excluir a empresa.");
      }

      setCompanies((previous) =>
        previous.filter((item) => item.companyId !== company.companyId)
      );
      toast.dismiss(toastId);
      toast.success("Empresa excluída com sucesso!");
    } catch (error: any) {
      toast.dismiss(toastId);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Não foi possível excluir a empresa.";
      toast.error(message);

      if (error?.response?.status === 403) {
        setCompanyDeletePermissionEnabled(false);
      }
    } finally {
      setDeleteCompanyLoadingId(null);
    }
  };

  useEffect(() => {
    handleListCompanies();
    handleLoadCompanyDeletePermission();
  }, [handleListCompanies, handleLoadCompanyDeletePermission]);

  return (
    <LayoutSubmaster>
      <div className="report-config-page management-page company-config-page">
        <section className="dashboard-hero report-hero mb-4">
          <div className="report-hero__content">
            <span className="pill-badge">Empresas</span>
            <h1 className="dashboard-title">Criar empresa</h1>
            <p className="dashboard-subtitle">
              Gerencie as empresas vinculadas ao submaster, com cadastro, edição e imagem.
            </p>
          </div>
          <div className="d-flex flex-wrap gap-2 epi-hero-actions">
            <div className="report-hero__badge">
              <i className="bi bi-buildings" />
              <div>
                <span>Empresas cadastradas</span>
                <strong>{filteredCompanies.length} registros visíveis</strong>
              </div>
            </div>
            {canCompanyCreate && <button className="btn btn-primary epi-primary-action" onClick={handleOpenCreate}>
              <i className="bi bi-plus-circle me-2"></i> Cadastrar empresa
            </button>}
          </div>
        </section>

        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <article className="dashboard-card metric-card soft report-metric-card">
              <div className="metric-card__icon">
                <i className="bi bi-building fs-4 text-primary" />
              </div>
              <div>
                <p className="metric-card__eyebrow">Total de empresas</p>
                <h2 className="metric-card__value">{companies.length}</h2>
                <p className="metric-card__caption">Registros disponíveis</p>
              </div>
            </article>
          </div>
          <div className="col-md-4">
            <article className="dashboard-card metric-card soft report-metric-card">
              <div className="metric-card__icon">
                <i className="bi bi-image fs-4 text-primary" />
              </div>
              <div>
                <p className="metric-card__eyebrow">Com imagem</p>
                <h2 className="metric-card__value">{companiesWithLogo}</h2>
                <p className="metric-card__caption">Logos cadastradas</p>
              </div>
            </article>
          </div>
          <div className="col-md-4">
            <article className="dashboard-card metric-card soft report-metric-card">
              <div className="metric-card__icon">
                <i className="bi bi-funnel fs-4 text-primary" />
              </div>
              <div>
                <p className="metric-card__eyebrow">Resultado atual</p>
                <h2 className="metric-card__value">{filteredCompanies.length}</h2>
                <p className="metric-card__caption">Linhas filtradas</p>
              </div>
            </article>
          </div>
        </div>

        <section className="section-panel report-filter-panel mb-4">
          <div className="section-heading">
            <div>
              <h3>Consulta de empresas</h3>
              <p>Busque por nome, CNPJ ou ID da empresa.</p>
            </div>
            <span className="pill-badge">{filteredCompanies.length} resultado(s)</span>
          </div>

          <div className="row g-3 align-items-end">
            <div className="col-lg-5 col-md-8">
              <label className="form-label">Pesquisar empresa</label>
              <div className="input-group report-input">
                <span className="input-group-text bg-white">
                  <i className="bi bi-search text-primary"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filtrar por empresa, CNPJ ou ID"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <button className="btn btn-outline-secondary w-100 report-clear-btn" onClick={() => setFilterText("")}>
                <i className="bi bi-arrow-counterclockwise me-1"></i>
                Limpar
              </button>
            </div>
          </div>
        </section>

        <section className="section-panel report-table-panel">
          <div className="section-heading">
            <div>
              <h3>Tabela de empresas</h3>
              <p>Visualize os dados principais e edite os registros cadastrados.</p>
            </div>
            <div className="report-table-panel__summary">
              <span className="pill-badge report-pill-success">{companiesWithLogo} com imagem</span>
              <span className="pill-badge report-pill-neutral">{companies.length} no total</span>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredCompanies}
            pagination
            progressPending={isLoading}
            highlightOnHover
            pointerOnHover
            responsive
            customStyles={customStyles}
            paginationComponentOptions={{
              rowsPerPageText: "Linhas por página",
              rangeSeparatorText: "de",
              selectAllRowsItem: true,
              selectAllRowsItemText: "Selecionar todos",
            }}
            noHeader
            noDataComponent={
              <div className="report-empty-state">
                <span className="report-empty-state__icon">
                  <i className="bi bi-buildings"></i>
                </span>
                <h4>Nenhuma empresa encontrada</h4>
                <p>Cadastre uma empresa para exibir os dados nesta tabela.</p>
              </div>
            }
          />
        </section>
      </div>

      {showForm && (
        <div className="modal fade show d-block" tabIndex={-1} aria-hidden="true">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content shadow-lg rounded-3 epi-modal">
              <div
                className="modal-header"
                style={{ background: "linear-gradient(90deg, #0d6efd, #5dade2)", color: "#fff" }}
              >
                <h5 className="modal-title" style={{ color: "white" }}>
                  <i className={`bi ${isUpdateMode ? "bi-pencil-square" : "bi-building-add"} me-2`}></i>
                  {isUpdateMode ? "Editar empresa" : "Cadastrar empresa"}
                </h5>
                <button
                  type="button"
                  className="mr-4 mt-4 btn-close btn-close-white"
                  onClick={handleCloseForm}
                ></button>
              </div>

              <div className="modal-body epi-modal__body">
                <form>
                  <div className="container">
                    <div className="row mb-4">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Nome da empresa</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="bi bi-building-fill text-primary"></i>
                          </span>
                          <input
                            type="text"
                            name="nameCompany"
                            className="form-control"
                            placeholder="Nome da empresa"
                            value={formData.nameCompany}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">CNPJ</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="bi bi-credit-card-2-front-fill text-secondary"></i>
                          </span>
                          <input
                            type="text"
                            name="cnpj"
                            className="form-control"
                            placeholder="CNPJ"
                            value={formData.cnpj}
                            onChange={handleChange}
                            maxLength={18}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="row mb-3 align-items-center">
                      <div className="col-md-7 mb-3">
                        <label className="form-label">Logo da empresa</label>
                        <div className="input-group">
                          <span className="input-group-text bg-white">
                            <i className="bi bi-image-fill text-info"></i>
                          </span>
                          <input
                            type="file"
                            name="submaster_logo_url"
                            className="form-control"
                            accept="image/*"
                            onChange={handleFileChange}
                          />
                        </div>
                      </div>

                      <div className="col-md-5 mb-3">
                        <div className="company-logo-preview">
                          {formData.logoPreview || formData.currentLogoUrl ? (
                            <img
                              src={formData.logoPreview || formData.currentLogoUrl}
                              alt="Logo da empresa"
                            />
                          ) : (
                            <span>
                              <i className="bi bi-building"></i>
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>

              <div className="modal-footer">
                {isUpdateMode && formData.currentLogoUrl && (
                  <button
                    type="button"
                    className="btn btn-outline-danger d-flex align-items-center"
                    onClick={() => handleDeleteImage(formData.companyId)}
                  >
                    <i className="bi bi-image-alt me-2"></i> Remover imagem
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-secondary d-flex align-items-center"
                  onClick={handleCloseForm}
                >
                  <i className="bi bi-x-circle me-2"></i> Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center"
                  onClick={handleSubmit}
                >
                  <i className="bi bi-check-circle me-2"></i>
                  {isUpdateMode ? "Atualizar" : "Cadastrar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </LayoutSubmaster>
  );
}

export default ConfigSubmasterCompanyPage;
